/* istanbul ignore file */

import { useCallback, useMemo, useRef } from 'react';
import { useMachine } from '@xstate/react';
import { useClient } from 'urql';

import { useLastInteractedStore } from '@order/LastInteractedStore';
import { getEnvVars } from '@order/utils';

import type {
  LocationSearchMachineContext,
  LocationSearchMachineEvents,
} from '../../machine';
import { createLocationSearchMachine } from '../../machine';
import { useLocationSearchNavigation } from '../useLocationSearchNavigation';
import { useLocationSearchTelemetry } from '../useLocationSearchTelemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitLocationSearchMachine = (
  props: UseInitLocationSearchMachine,
) => {
  const { minimalCharactersForSearch } = props;

  const client = useClient();
  const lastInteractedStore = useLastInteractedStore();
  const locationSearchNavigation = useLocationSearchNavigation();
  const {
    trackLocationSearchType,
    trackLocationSearchByArea,
    trackLocationSearchByString,
    trackLocationSearchByDeliveryPlaceID,
    trackFocusedLocationPinId,
    trackNavigateToLocationMenu,
    trackNavigateToDeliveryLocationMenu,
  } = useLocationSearchTelemetry();

  // ─────────────────────────────────────────────────────────────────────

  const {
    locationSearchRouteParam,
    setLocationSearchTypeRouteParam,
    navigateToLocation,
    navigateToDeliveryLocation,
  } = locationSearchNavigation;

  const lastInteractedStoreAddressId = lastInteractedStore?.addressId;

  // ─── Actions ─────────────────────────────────────────────────────────

  const onLocationTypeChangeEvent = useCallback(
    (context: LocationSearchMachineContext) => {
      setLocationSearchTypeRouteParam(context);
      trackLocationSearchType(context);
    },
    [setLocationSearchTypeRouteParam, trackLocationSearchType],
  );

  const onNavigateToLocationEvent = useCallback(
    (context: LocationSearchMachineContext, event: NavigateToLocationEvent) => {
      trackNavigateToLocationMenu(event?.entryPoint)(context);
    },
    [trackNavigateToLocationMenu],
  );

  const onNavigateToDeliveryLocationEvent = useCallback(
    (
      context: LocationSearchMachineContext,
      event: NavigateToDeliveryLocationEvent,
    ) => {
      trackNavigateToDeliveryLocationMenu(event?.entryPoint)(context);
    },
    [trackNavigateToDeliveryLocationMenu],
  );

  const onLocationWarningConfirmEvent = useMemo(
    () => trackNavigateToLocationMenu('location-warning'),
    [trackNavigateToLocationMenu],
  );

  // ─── Initialize Machine ──────────────────────────────────────────────

  const { current: LocationSearchMachine } = useRef(
    createLocationSearchMachine({
      client,
      lastInteractedStoreAddressId,
      defaultLocationSearchType: locationSearchRouteParam,
      minimalCharactersForSearch,
      shouldUseCurrentPosition: true,
      googlePlacesApiKey: GOOGLE_API_KEY,
    }),
  );

  return useMachine(LocationSearchMachine, {
    actions: {
      navigateToLocation,
      navigateToDeliveryLocation,

      // ─── Event Listeners ─────────────────────────────────────────────────

      onLocationTypeChangeEvent,
      onNavigateToLocationEvent,
      onNavigateToDeliveryLocationEvent,
      onLocationWarningConfirmEvent,
      onSearchByAreaEvent: trackLocationSearchByArea,
      onSearchByStringEvent: trackLocationSearchByString,
      onSearchByDeliveryPlaceIdEvent: trackLocationSearchByDeliveryPlaceID,
      onFocusedPinIdChangeEvent: trackFocusedLocationPinId,
    },
  });
};

// ─────────────────────────────────────────────────────────────────────────────

const { GOOGLE_API_KEY } = getEnvVars();

// ─── Types ───────────────────────────────────────────────────────────────────

type UseInitLocationSearchMachine = {
  readonly minimalCharactersForSearch: number;
};

type NavigateToLocationEvent = Extract<
  LocationSearchMachineEvents,
  { readonly type: 'NAVIGATE_TO_LOCATION' }
>;

type NavigateToDeliveryLocationEvent = Extract<
  LocationSearchMachineEvents,
  { readonly type: 'NAVIGATE_TO_DELIVERY_LOCATION' }
>;
